/**
 * Reference notes beside an AI reply suggestion (cs-analysis
 * docs/design-client-comms.md §8.1b, 2026-10-22). Nikhil: "reps see INTERNAL
 * knowledge units as REFERENCE NOTES beside a reply draft, NEVER inside the
 * draft text". POST /comms/channels/:id/suggest-reply now also returns
 * `referenceNotes[{id, module, feature, title, body, confidence, status}]` —
 * internal facts (a known defect, a workaround, an internal process) for the
 * rep to read. They are not what grounded the draft (`knowledge` is), the
 * drafter never saw them, and accepting the suggestion copies only its text.
 * Pure helpers; ReferenceNotes.jsx renders them; reference_notes.test.js.
 */

import { language_of } from './reply_language';

/** At most this many notes shown (the api sends up to 3). */
export const REFERENCE_NOTES_MAX = 3;
export const REFERENCE_NOTES_LABEL = 'Internal reference — for representatives, not the client';
export const REFERENCE_NOTES_HINT = 'Internal knowledge relevant to this message (a known defect, a workaround, an internal process), for representatives only. It is not in the draft and is never sent; use it to decide what to say.';

const str_or_null = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num_or_null = (v) => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

/** The response's notes: well-formed ones only (an id, a title or a body), at most REFERENCE_NOTES_MAX; [] for an older api. */
export const normalize_reference_notes = (v) => (Array.isArray(v) ? v : [])
    .filter((n) => n && (typeof n.id === 'string' || typeof n.id === 'number') && (str_or_null(n.title) || str_or_null(n.body)))
    .slice(0, REFERENCE_NOTES_MAX)
    .map((n) => ({
        id: String(n.id),
        module: str_or_null(n.module),
        feature: str_or_null(n.feature),
        title: str_or_null(n.title) || '',
        body: typeof n.body === 'string' ? n.body : '',
        confidence: num_or_null(n.confidence),
        status: str_or_null(n.status),
    }));

/** The suggestion state the thread keeps from a suggest-reply response: the draft, its grounding, and — apart — the notes. */
export const suggestion_of = (r, for_seq) => ({
    text: r.suggestion,
    confidence: r.confidence,
    evidenceCount: r.evidenceCount || 0,
    knowledgeCount: r.knowledgeCount || 0,
    knowledge: r.knowledge || [],
    referenceNotes: normalize_reference_notes(r.referenceNotes),
    forSeq: for_seq,
    // The draft's language, why, and the chat's remembered pick (reply_language.js; T006).
    ...language_of(r),
});

/** What accepting a suggestion puts in the composer: its text, never a note. */
export const accepted_draft = (suggestion) => (suggestion && typeof suggestion.text === 'string' ? suggestion.text : '');

/** Whether an accepted suggestion leaves anything to show under the composer (its grounding chips or its notes). */
export const keeps_beside = (suggestion) => !!suggestion && ((suggestion.knowledge && suggestion.knowledge.length > 0) || (suggestion.referenceNotes && suggestion.referenceNotes.length > 0));
